<script>
  import { createEventDispatcher } from "svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import BpmnPreview from "./BpmnPreview.svelte";

  export let open = false;
  export let sideLabel = "A";

  const dispatch = createEventDispatcher();

  let tree = [];
  let expandedFolders = {};
  let selectedModel = null;
  let loadingTree = false;
  let loadingModel = false;
  let error = "";
  let requestId = 0;
  let wasOpen = false;
  let searchQuery = "";
  let lastSearchQuery = "";

  $: filteredTree = getFilteredTree(tree, searchQuery);
  $: visibleRows = getVisibleRows(filteredTree, 0, [], expandedFolders);
  $: matchingModels = getMatchingModels(tree, searchQuery);
  $: treeError = error && !tree.length && !loadingTree;
  $: previewError = error && tree.length && !loadingModel;
  $: if (open !== wasOpen) {
    wasOpen = open;
    if (open) openPicker();
  }
  $: if (tree.length && searchQuery !== lastSearchQuery) applySearch();

  function getVisibleRows(
    nodes,
    depth = 0,
    rows = [],
    expanded = expandedFolders,
    continuationLevels = [],
  ) {
    const visibleNodes = (nodes || []).filter(hasSelectableContent);
    visibleNodes.forEach((node, index) => {
      const isLast = index === visibleNodes.length - 1;
      rows.push({
        node,
        depth,
        isLast,
        continuationLevels,
      });
      if (
        node.type === "folder" &&
        expanded[node.id] &&
        node.children?.length
      ) {
        const childContinuationLevels =
          !isLast && depth > 0
            ? [...continuationLevels, depth - 1]
            : continuationLevels;
        getVisibleRows(
          node.children,
          depth + 1,
          rows,
          expanded,
          childContinuationLevels,
        );
      }
    });
    return rows;
  }

  function hasSelectableContent(node) {
    if (node.type === "process") return true;
    return Boolean(node.children?.some(hasSelectableContent));
  }

  function normalizeSearch(value = "") {
    return value.toLowerCase().trim();
  }

  function getNodeName(node) {
    return node.name || node.text || "";
  }

  function matchesSearch(node, query) {
    if (node.type !== "process") return false;
    return normalizeSearch(`${getNodeName(node)}.bpmn`).includes(query);
  }

  function getFilteredTree(nodes, search) {
    const query = normalizeSearch(search);
    if (!query) return nodes;

    return (nodes || [])
      .map((node) => {
        if (node.type === "process") return matchesSearch(node, query) ? node : null;
        const children = getFilteredTree(node.children, search);
        return children.length ? { ...node, children } : null;
      })
      .filter(Boolean);
  }

  function getMatchingModels(nodes, search, matches = []) {
    const query = normalizeSearch(search);
    if (!query) return matches;

    (nodes || []).forEach((node) => {
      if (node.type === "process" && matchesSearch(node, query)) {
        matches.push(node);
      } else if (node.type === "folder") {
        getMatchingModels(node.children, search, matches);
      }
    });
    return matches;
  }

  function getFolderIds(nodes, ids = {}) {
    (nodes || []).forEach((node) => {
      if (node.type !== "folder") return;
      ids[node.id] = true;
      getFolderIds(node.children, ids);
    });
    return ids;
  }

  function applySearch() {
    lastSearchQuery = searchQuery;
    if (!normalizeSearch(searchQuery)) {
      expandedFolders = initialExpandedFolders(tree);
      return;
    }

    expandedFolders = {
      ...expandedFolders,
      ...getFolderIds(filteredTree),
    };
    if (matchingModels.length === 1) {
      previewModel(matchingModels[0]);
    }
  }

  function getConnectorLeft(level) {
    return 1.375 + level * 1.5;
  }

  function getRowStyle(depth) {
    return `padding-left: ${0.75 + depth * 1.5}rem`;
  }

  function getContinuationStyle(level) {
    return `left: ${getConnectorLeft(level)}rem`;
  }

  function getBranchStyle(depth, isLast) {
    const left = getConnectorLeft(depth - 1);
    const width = getConnectorLeft(depth) - left - 0.625;
    return `--connector-bottom: ${isLast ? "50%" : "0"}; left: ${left}rem; width: ${width}rem`;
  }

  function getTailStyle(depth) {
    return `left: ${getConnectorLeft(depth)}rem`;
  }

  function hasChildren(node) {
    return Boolean(node.children?.some(hasSelectableContent));
  }

  function initialExpandedFolders(nodes) {
    return (nodes || []).reduce((expanded, node) => {
      if (node.type === "folder") expanded[node.id] = true;
      return expanded;
    }, {});
  }

  async function openPicker() {
    selectedModel = null;
    searchQuery = "";
    lastSearchQuery = "";
    error = "";
    loadingTree = true;
    const currentRequest = ++requestId;

    try {
      const response = await fetchWithSession("/api/pman/processFolders");
      if (!response.ok) throw new Error("Could not load saved process models.");
      const data = await response.json();
      if (currentRequest !== requestId) return;
      tree = Array.isArray(data) ? data : [];
      expandedFolders = initialExpandedFolders(tree);
    } catch (err) {
      if (currentRequest !== requestId) return;
      tree = [];
      error = err.message || "Could not load saved process models.";
    } finally {
      if (currentRequest === requestId) loadingTree = false;
    }
  }

  function toggleFolder(id) {
    expandedFolders = {
      ...expandedFolders,
      [id]: !expandedFolders[id],
    };
  }

  function formatStoredDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return `${String(date.getDate()).padStart(2, "0")}.${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}.${date.getFullYear()}`;
  }

  async function previewModel(node) {
    const currentRequest = ++requestId;
    loadingModel = true;
    error = "";
    selectedModel = null;

    try {
      const response = await fetchWithSession(
        `/api/pman/processModel/${encodeURIComponent(node.id)}`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error("Could not load this BPMN model.");
      const process = await response.json();
      if (currentRequest !== requestId) return;
      selectedModel = {
        id: process.id,
        name: process.name || node.name || node.text || "Process Model",
        date: formatStoredDate(process.created_at),
        bpmnXml: process.bpmnstring,
        avgTimeSeconds:
          process.avg_time_seconds == null
            ? null
            : Number(process.avg_time_seconds),
        activityMetrics: Array.isArray(process.activity_metrics)
          ? process.activity_metrics
          : [],
      };
    } catch (err) {
      if (currentRequest !== requestId) return;
      error = err.message || "Could not load this BPMN model.";
    } finally {
      if (currentRequest === requestId) loadingModel = false;
    }
  }

  function confirmSelection() {
    if (!selectedModel?.bpmnXml) return;
    dispatch("select", selectedModel);
    open = false;
  }
</script>

<svelte:window
  on:keydown={(event) => {
    if (open && event.key === "Escape") open = false;
  }}
/>

{#if open}
  <div
    class="model_picker_backdrop"
    role="presentation"
    on:click={() => (open = false)}
    on:keydown={(event) => {
      if (event.key === "Escape") open = false;
    }}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <section
      class="model_picker_dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="process-model-picker-heading"
      on:click|stopPropagation
    >
      <header class="model_picker_header">
        <div>
          <h2 id="process-model-picker-heading">Select Process Model {sideLabel}</h2>
          <p>Choose the process model you want to compare.</p>
        </div>
        <button
          type="button"
          class="dialog_close"
          aria-label="Close process model selection"
          on:click={() => (open = false)}
        >
          <img src="/img/assistant/icons/close_dark.svg" alt="" />
        </button>
      </header>

      <div class="model_picker">
    <aside class="model_tree" aria-label="Saved process models">
      <div class="tree_heading">Process Selection</div>
      <div class="tree_search">
        <input
          type="search"
          bind:value={searchQuery}
          placeholder="Search model"
          aria-label="Search process model"
        />
      </div>

      <div class="tree_body">
        {#if loadingTree}
          <div class="tree_state">Loading process models…</div>
        {:else if treeError}
          <div class="tree_state">Process models could not be loaded.</div>
        {:else if !tree.length}
          <div class="tree_state">No process models have been saved yet.</div>
        {:else if !filteredTree.length}
          <div class="tree_state">No process models match the search.</div>
        {:else}
          <div class="tree_rows" role="tree">
            {#each visibleRows as row (row.node.id)}
              {#if row.node.type === "folder"}
                <button
                  type="button"
                  class="tree_row folder_row"
                  class:indented={row.depth > 0}
                  style={getRowStyle(row.depth)}
                  aria-expanded={hasChildren(row.node) ? Boolean(expandedFolders[row.node.id]) : undefined}
                  on:click={() => {
                    if (hasChildren(row.node)) toggleFolder(row.node.id);
                  }}
                >
                  <span class="tree_connectors" aria-hidden="true">
                    {#each row.continuationLevels as level}
                      <span
                        class="tree_connector_continue"
                        style={getContinuationStyle(level)}
                      ></span>
                    {/each}
                    {#if row.depth > 0}
                      <span
                        class="tree_connector_branch"
                        style={getBranchStyle(row.depth, row.isLast)}
                      ></span>
                    {/if}
                  </span>
                  {#if hasChildren(row.node) && expandedFolders[row.node.id]}
                    <span
                      class="folder_connector_tail"
                      style={getTailStyle(row.depth)}
                      aria-hidden="true"
                    ></span>
                  {/if}
                  <span
                    class="tree_icon folder_icon"
                    class:open={expandedFolders[row.node.id]}
                    aria-hidden="true"
                  ></span>
                  <span class="node_label">{row.node.name || row.node.text}</span>
                  <span class="level_tag">L{Math.min(row.depth + 1, 4)}</span>
                </button>
              {:else if row.node.type === "process"}
                <button
                  type="button"
                  class="tree_row process_row"
                  class:selected={selectedModel?.id == row.node.id}
                  class:indented={row.depth > 0}
                  style={getRowStyle(row.depth)}
                  on:click={() => previewModel(row.node)}
                >
                  <span class="tree_connectors" aria-hidden="true">
                    {#each row.continuationLevels as level}
                      <span
                        class="tree_connector_continue"
                        style={getContinuationStyle(level)}
                      ></span>
                    {/each}
                    {#if row.depth > 0}
                      <span
                        class="tree_connector_branch"
                        style={getBranchStyle(row.depth, row.isLast)}
                      ></span>
                    {/if}
                  </span>
                  <span class="tree_icon model_icon" aria-hidden="true"></span>
                  <span class="node_label">{row.node.name || row.node.text}.bpmn</span>
                  <span class="model_date"
                    >{formatStoredDate(row.node.created_at) || "–"}</span
                  >
                </button>
              {/if}
            {/each}
          </div>
        {/if}
      </div>
    </aside>

    <section class="model_live_view" aria-label="Process model preview">
      <div class="preview_heading">
        <span>Preview</span>
        {#if selectedModel}<strong>{selectedModel.name}.bpmn</strong>{/if}
      </div>
      <div class="preview_body">
        {#if loadingModel}
          <div class="preview_state">Loading preview…</div>
        {:else if previewError}
          <div class="preview_state">Preview could not be loaded. Please select another process model.</div>
        {:else if selectedModel?.bpmnXml}
          <BpmnPreview
            bpmnXml={selectedModel.bpmnXml}
            actionsRightOffset="0.6rem"
            actionsBottomOffset="0.6rem"
            showFullscreen={false}
          />
        {:else}
          <div class="preview_state">Select a process model to see its preview.</div>
        {/if}
      </div>
    </section>
      </div>

      <footer class="model_picker_footer">
        <button
          type="button"
          class="cancel_button"
          on:click={() => (open = false)}
        >
          Cancel
        </button>
        <button
          type="button"
          class="select_button"
          disabled={!selectedModel?.bpmnXml}
          on:click={confirmSelection}
        >
          Select
        </button>
      </footer>
    </section>
  </div>
{/if}

<style>
  .model_picker_backdrop {
    align-items: center;
    background: rgba(22, 22, 22, 0.5);
    display: flex;
    inset: 0;
    justify-content: center;
    padding: 1rem;
    position: fixed;
    z-index: 10000;
  }

  .model_picker_dialog {
    background: var(--pax-white);
    border-radius: 0.5rem;
    box-shadow: 0 1rem 2.5rem rgba(24, 28, 34, 0.25);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    max-height: calc(100vh - 2rem);
    max-width: 1500px;
    overflow: hidden;
    padding: 1rem;
    width: calc(100vw - 2rem);
  }

  .model_picker_header {
    align-items: flex-start;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding: 0 0 1rem;
  }

  .model_picker_header h2,
  .model_picker_header p {
    margin: 0;
  }

  .model_picker_header h2 {
    font-size: 0.95rem;
  }

  .model_picker_header p {
    color: #6f7780;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .dialog_close {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 0.5rem;
    cursor: pointer;
    display: flex;
    height: 2.5rem;
    justify-content: center;
    width: 2.5rem;
  }

  .dialog_close:hover {
    background: var(--pax-smoke);
  }

  .dialog_close img {
    height: 0.8rem;
    width: 0.8rem;
  }

  .model_picker_footer {
    border-top: 1px solid var(--border-color);
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    margin-top: 1rem;
    padding-top: 1rem;
  }

  .model_picker_footer button {
    border: 0;
    border-radius: 0.5rem;
    color: var(--pax-white);
    cursor: pointer;
    height: 3rem;
    min-width: 7rem;
    padding: 0 1rem;
  }

  .cancel_button {
    background: var(--pax-black);
  }

  .select_button {
    background: var(--pax-orange);
  }

  .select_button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .model_picker {
    border: var(--bw) solid var(--pax-black);
    border-radius: 0.5rem;
    display: grid;
    grid-template-columns: minmax(18rem, 28%) minmax(0, 1fr);
    height: min(68vh, 690px);
    overflow: hidden;
    width: 100%;
  }

  .model_tree {
    border-right: var(--bw) solid var(--pax-black);
    display: grid;
    grid-template-rows: 3rem 3rem minmax(0, 1fr);
    min-width: 0;
    overflow: hidden;
  }

  .tree_heading,
  .preview_heading {
    align-items: center;
    background: var(--pax-white);
    border-bottom: var(--bw) solid var(--pax-black);
    display: flex;
    font-size: 0.85rem;
    font-weight: 700;
    height: 3rem;
    justify-content: space-between;
    padding: 0 1rem;
  }

  .preview_heading strong {
    font-size: 0.8rem;
    font-weight: 500;
    max-width: 70%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tree_search {
    align-items: center;
    border-bottom: var(--bw) solid var(--pax-black);
    display: flex;
    padding: 0.5rem 0.75rem;
  }

  .tree_search input {
    border: var(--bw) solid var(--border-color);
    border-radius: 0.35rem;
    color: var(--pax-black);
    font-size: 0.85rem;
    height: 2rem;
    min-width: 0;
    padding: 0 0.6rem;
    width: 100%;
  }

  .tree_search input:focus {
    border-color: var(--pax-black);
    outline: none;
  }

  .tree_body {
    min-height: 0;
    overflow: hidden;
  }

  .tree_rows {
    height: 100%;
    overflow: auto;
    padding: 0.5rem 0;
  }

  .tree_row {
    align-items: center;
    background: transparent;
    border: 0;
    color: var(--pax-black);
    cursor: pointer;
    display: flex;
    gap: 0.5rem;
    height: 2.5rem;
    padding-right: 0.75rem;
    position: relative;
    text-align: left;
    width: 100%;
  }

  .tree_row:hover,
  .tree_row.selected {
    background: #f1f3f5;
  }

  .tree_row.selected {
    box-shadow: inset 3px 0 0 var(--pax-orange);
  }

  .tree_connectors {
    inset: 0;
    pointer-events: none;
    position: absolute;
    z-index: 0;
  }

  .tree_connector_continue,
  .tree_connector_branch::before,
  .folder_connector_tail {
    border-left: 0.5px solid var(--pax-black);
    position: absolute;
    width: 0;
  }

  .tree_connector_continue {
    bottom: 0;
    top: 0;
  }

  .tree_connector_branch {
    bottom: 0;
    position: absolute;
    top: 0;
  }

  .tree_connector_branch::before {
    bottom: var(--connector-bottom);
    content: "";
    left: 0;
    top: 0;
  }

  .tree_connector_branch::after {
    border-top: 0.5px solid var(--pax-black);
    content: "";
    height: 0;
    left: 0;
    position: absolute;
    top: 50%;
    width: 100%;
  }

  .tree_icon {
    flex: 0 0 1.25rem;
    height: 1.25rem;
    object-fit: contain;
    position: relative;
    width: 1.25rem;
    z-index: 1;
  }

  .folder_icon {
    background: var(--pax-black);
    mask: url("/img/assistant/icons/folder.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/folder.svg") center / contain no-repeat;
  }

  .folder_icon.open {
    mask-image: url("/img/assistant/icons/folder_open.svg");
    -webkit-mask-image: url("/img/assistant/icons/folder_open.svg");
  }

  .folder_connector_tail {
    bottom: -0.5px;
    top: calc(50% + 0.625rem);
  }

  .model_icon {
    background: var(--pax-black);
    mask: url("/img/assistant/icons/graph_2.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/graph_2.svg") center / contain no-repeat;
  }

  .node_label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
    text-overflow: ellipsis;
    white-space: nowrap;
    z-index: 1;
  }

  .level_tag {
    color: #6f7780;
    font-size: 0.7rem;
    font-weight: 700;
    position: relative;
    z-index: 1;
  }

  .model_date {
    color: #6f7780;
    flex: 0 0 auto;
    font-size: 0.7rem;
    position: relative;
    z-index: 1;
  }

  .model_live_view {
    display: grid;
    grid-template-rows: 3rem minmax(0, 1fr);
    min-width: 0;
  }

  .preview_body {
    min-height: 0;
    position: relative;
  }

  .preview_state,
  .tree_state {
    align-items: center;
    color: #6f7780;
    display: flex;
    height: 100%;
    justify-content: center;
    min-height: 0;
    padding: 1rem;
    text-align: center;
  }

  .preview_state {
    height: 100%;
  }

  @media (max-width: 800px) {
    .model_picker {
      grid-template-columns: 1fr;
      grid-template-rows: minmax(12rem, 38%) minmax(0, 1fr);
    }

    .model_tree {
      border-bottom: var(--bw) solid var(--pax-black);
      border-right: 0;
    }
  }
</style>
